export const PIPELINE_CONSTANTS = {
    PREPARE_SECONDS: 30,
    BUFFER_SECONDS: 25,
    STEP_TITLES: ["Prepare", "Start", "Forfeit", "Finish"],
};
/**
 * Determines the status of a pipeline step
 */
export function getStepStatus(tournament, roundNumber, stepNumber) {
    if (stepNumber < 0)
        return "scheduled";
    if (tournament.currentRound > roundNumber)
        return "skipped";
    return "scheduled";
}
/**
 * Calculates the start time for a pipeline step
 */
export function calculateStepStartTime(stepTitle, baseTimeControl, roundStartTime) {
    const { PREPARE_SECONDS, BUFFER_SECONDS } = PIPELINE_CONSTANTS;
    switch (stepTitle) {
        case "Prepare":
            return roundStartTime;
        case "Start":
            return roundStartTime + PREPARE_SECONDS * 1000;
        case "Forfeit":
            return (roundStartTime +
                (baseTimeControl * 60 + PREPARE_SECONDS + BUFFER_SECONDS) * 1000);
        case "Finish":
            return (roundStartTime +
                (baseTimeControl * 60 * 2 + PREPARE_SECONDS + BUFFER_SECONDS * 2) * 1000);
        default:
            return roundStartTime;
    }
}
/**
 * Calculates the wait seconds for a pipeline step
 */
export function calculateStepWaitSeconds(stepTitle, baseTimeControl, roundMode, roundNumber) {
    const { PREPARE_SECONDS, BUFFER_SECONDS } = PIPELINE_CONSTANTS;
    switch (stepTitle) {
        case "Prepare":
            return roundMode === "Auto" && roundNumber > 1 ? PREPARE_SECONDS : null;
        case "Start":
            return roundMode === "Auto" ? PREPARE_SECONDS : null;
        case "Forfeit":
            return baseTimeControl * 60 + BUFFER_SECONDS;
        case "Finish":
            return roundMode === "Auto"
                ? baseTimeControl * 60 + BUFFER_SECONDS
                : null;
        default:
            return null;
    }
}
/**
 * Creates pipeline steps from rounds without time calculations
 */
export function createSteps(tournament, rounds) {
    let stepIndex = 0;
    return rounds.flatMap((round) => PIPELINE_CONSTANTS.STEP_TITLES.map((title, stepNumber) => ({
        index: stepIndex++,
        roundNumber: round.roundNumber,
        stepNumber: stepNumber + 1,
        title,
        status: getStepStatus(tournament, round.roundNumber, stepNumber + 1),
        startTime: 0, // Will be set by syncSteps
        waitSeconds: null, // Will be set by syncSteps
        description: `${title} Round ${round.roundNumber}`,
    })));
}
/**
 * Synchronizes step times based on current round start times
 */
export function syncSteps(steps, tournament, rounds) {
    const updates = {};
    steps.forEach((step) => {
        if (step.status !== "scheduled")
            return;
        const round = rounds[step.roundNumber - 1];
        if (!round?.startTime)
            return;
        const roundStartTime = round.startTime.toDate().getTime();
        updates[step.index.toString()] = {
            startTime: calculateStepStartTime(step.title, tournament.baseTimeControl, roundStartTime),
            waitSeconds: calculateStepWaitSeconds(step.title, tournament.baseTimeControl, tournament.roundMode || "Manual", step.roundNumber),
        };
    });
    return updates;
}
/**
 * Creates steps with time synchronization in one call
 */
export function createAndSyncSteps(tournament, rounds) {
    const steps = createSteps(tournament, rounds);
    const timeUpdates = syncSteps(steps, tournament, rounds);
    return steps.map((step) => {
        const update = timeUpdates[step.index.toString()];
        if (update) {
            return {
                ...step,
                startTime: update.startTime,
                waitSeconds: update.waitSeconds,
            };
        }
        return step;
    });
}
